import { StopOutlined } from '@ant-design/icons'
import clsx from 'clsx'
import moment from 'moment'

import { getMilestoneStatusColor } from './get-milestone-status-color'
import { ObjectItem } from '../../../../entities/objects/types/object.types'
import { MilestoneType } from '../../../../entities/workflow/constants/workflow.constants'
import { Checklist, MilestoneItem } from '../../../../entities/workflow/types/workflow.types'
import { FormItemType } from '../../../shared/form-builder/constants/form-builder.constants'
import styles from '../components/workspace-table/workspace-table.module.scss'
import { WorkspaceTableMode, WorkspaceWorkMode } from '../constants/workspace.constants'

export const getWorkspaceTableColumns = (
  milestones: MilestoneItem[],
  checklistMap: Map<string, Checklist>,
  mode: WorkspaceWorkMode,
  t: any,
  checkedMilestones: any,
  setSelectedMilestone: any,
  setSelectedObject: any,
  tableMode: WorkspaceTableMode,
) => {
  const milestonesToShow = milestones.filter((milestone) => checkedMilestones.has(milestone.id))

  if (tableMode === WorkspaceTableMode.Full) {
    return getAllColumns(milestonesToShow, checklistMap, mode, t, setSelectedMilestone, setSelectedObject)
  } else {
    return getDatesColumns(milestonesToShow, checklistMap, mode, t, setSelectedMilestone, setSelectedObject)
  }
}

function getAllColumns(
  milestonesToShow: MilestoneItem[],
  checklistMap: Map<string, Checklist>,
  mode: WorkspaceWorkMode,
  t: any,
  setSelectedMilestone: any,
  setSelectedObject: any,
) {
  return [
    {
      title: t('workspace.table.objectsHeader'),
      dataIndex: 'name',
      key: 'name',
    },
    ...milestonesToShow.map((filteredMilestone) => ({
      title: filteredMilestone.name,
      key: String(filteredMilestone.id),
      onHeaderCell: () => ({
        className: filteredMilestone.type === MilestoneType.Milestone ? styles.milestoneHeader : styles.activityHeader,
      }),
      children: [
        {
          title: t('workspace.table.factStart'),
          key: String(filteredMilestone.id) + '_fact_start',
          render: (record: ObjectItem) => {
            const checklistValue = checklistMap.get(`${filteredMilestone.id}-${record.id}`)

            let date = ''
            if (checklistValue?.factStart) {
              date = moment(checklistValue.factStart).format('YYYY-MM-DD')
            }

            return <span>{date}</span>
          },
        },
        {
          title: t('workspace.table.factFinish'),
          key: String(filteredMilestone.id) + '_fact_finish',
          render: (record: ObjectItem) => {
            const checklistValue = checklistMap.get(`${filteredMilestone.id}-${record.id}`)

            let date = ''
            if (checklistValue?.factFinish) {
              date = moment(checklistValue.factFinish).format('YYYY-MM-DD')
            }

            return <span>{date}</span>
          },
        },
        ...filteredMilestone.checkList
          .filter((checklist) => checklist.type !== FormItemType.Header)
          .map((checklist) => ({
            title: checklist.label,
            key: String(checklist.id),
            render: (record: ObjectItem) => {
              const checklistValue = checklistMap.get(`${filteredMilestone.id}-${record.id}`)
              const checklistItemValue = checklistValue?.checklist[checklist.id]

              if (Array.isArray(checklistItemValue)) {
                return <span>{checklistItemValue.join('')}</span>
              }

              return <span>{checklistItemValue}</span>
            },
            onCell: (record: ObjectItem) => {
              return {
                className: clsx(styles.milestoneCell, styles.milestoneCellCentered),
                onClick: () => {
                  setSelectedMilestone(filteredMilestone)
                  setSelectedObject(record)
                },
              }
            },
          })),
      ],
    })),
  ]
}

function getDatesColumns(
  milestonesToShow: MilestoneItem[],
  checklistMap: Map<string, Checklist>,
  mode: WorkspaceWorkMode,
  t: any,
  setSelectedMilestone: any,
  setSelectedObject: any,
) {
  return [
    {
      title: t('workspace.table.objectsHeader'),
      dataIndex: 'name',
      key: 'name',
    },
    ...milestonesToShow.map((filteredMilestone) => ({
      title: filteredMilestone.name,
      key: String(filteredMilestone.id),
      onHeaderCell: () => ({
        className: filteredMilestone.type === MilestoneType.Milestone ? styles.milestoneHeader : styles.activityHeader,
      }),
      render: (record: ObjectItem) => {
        const checklist = checklistMap.get(`${filteredMilestone.id}-${record.id}`)

        const startDate = checklist?.factStart ? moment(checklist?.factStart).format('DD.MM.YYYY') : null
        const endDate = checklist?.factFinish ? moment(checklist?.factFinish).format('DD.MM.YYYY') : null

        const statusIndicator =
          mode === WorkspaceWorkMode.Checking && !filteredMilestone.shouldBeChecked ? (
            <span className={styles.statusIndicator}>
              <StopOutlined />
            </span>
          ) : (
            <span
              className={styles.statusIndicator}
              style={{ background: getMilestoneStatusColor(checklist?.status) }}
            ></span>
          )

        if (startDate && endDate) {
          return (
            <div className="d-flex align-items-center">
              {statusIndicator}
              <span>
                {startDate} - {endDate}
              </span>
            </div>
          )
        }

        return (
          <div className="d-flex align-items-center">
            {statusIndicator}
            <span>{startDate}</span>
          </div>
        )
      },
      onCell: (record: ObjectItem) => {
        return {
          className: styles.milestoneCell,
          onClick: () => {
            setSelectedMilestone(filteredMilestone)
            setSelectedObject(record)
          },
        }
      },
    })),
  ]
}
